import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ChatConversation } from '../../models/chat.models';
import { AgentDefinition } from '../../models/agent.models';

interface ConversationGroup {
  label: string;
  items: ChatConversation[];
}

@Component({
  selector: 'app-chat-sidebar',
  templateUrl: './chat-sidebar.component.html',
  styleUrls: ['./chat-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatSidebarComponent implements AfterViewChecked {
  @Input() conversations: ReadonlyArray<ChatConversation> = [];
  @Input() activeId: string | null = null;
  @Output() readonly pick = new EventEmitter<string>();
  @Output() readonly newChat = new EventEmitter<void>();
  @Output() readonly rename = new EventEmitter<{ id: string; title: string }>();
  @Output() readonly deleteChat = new EventEmitter<string>();

  // Agent Hub
  @Input() agents: AgentDefinition[] = [];
  @Output() readonly tabChange = new EventEmitter<'chat' | 'agents'>();

  activeTab: 'chat' | 'agents' = 'chat';
  filterMode: 'all' | 'today' | 'week' = 'all';

  @ViewChildren('titleInput') private readonly titleInputs!: QueryList<ElementRef<HTMLInputElement>>;

  query = '';
  editingId: string | null = null;
  editingValue = '';
  confirmingId: string | null = null;

  private focusPendingId: string | null = null;

  readonly titleMaxLength = 80;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  onDelete(event: MouseEvent, id: string): void {
    event.stopPropagation();
    this.confirmingId = id;
    this.cdr.markForCheck();
  }

  onConfirmDelete(id: string): void {
    this.confirmingId = null;
    this.deleteChat.emit(id);
  }

  onCancelDelete(): void {
    this.confirmingId = null;
    this.cdr.markForCheck();
  }

  get groups(): ConversationGroup[] {
    const q = this.query.trim().toLowerCase();
    let filtered = q
      ? this.conversations.filter((c) => c.title.toLowerCase().includes(q))
      : [...this.conversations];
    const today = this.today();
    const yesterday = new Date(today.getTime() - 86400000);
    const weekAgo = new Date(today.getTime() - 7 * 86400000);

    if (this.filterMode === 'today') {
      filtered = filtered.filter((c) => this.sameDay(new Date(c.updatedAt), today));
    } else if (this.filterMode === 'week') {
      filtered = filtered.filter((c) => new Date(c.updatedAt) >= weekAgo);
    }
    const groups: Record<string, ChatConversation[]> = { today: [], yesterday: [], older: [] };
    for (const c of filtered) {
      const d = new Date(c.updatedAt);
      if (this.sameDay(d, today)) groups['today'].push(c);
      else if (this.sameDay(d, yesterday)) groups['yesterday'].push(c);
      else groups['older'].push(c);
    }
    const out: ConversationGroup[] = [];
    if (groups['today'].length) out.push({ label: 'chatAi.sidebar.today', items: groups['today'] });
    if (groups['yesterday'].length) out.push({ label: 'chatAi.sidebar.yesterday', items: groups['yesterday'] });
    if (groups['older'].length) out.push({ label: 'chatAi.sidebar.older', items: groups['older'] });
    return out;
  }

  trackById(_index: number, c: ChatConversation): string { return c.id; }
  trackByLabel(_index: number, g: ConversationGroup): string { return g.label; }

  onTitleClick(event: MouseEvent, c: ChatConversation): void {
    // Only enter edit mode if this is the active conversation — otherwise the
    // click should switch conversations, which is the sidebar's primary role.
    if (c.id !== this.activeId) return;
    event.stopPropagation();
    this.startEdit(c);
  }

  startEdit(c: ChatConversation): void {
    this.editingId = c.id;
    this.editingValue = c.title;
    this.focusPendingId = c.id;
  }

  onEditKey(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  commitEdit(): void {
    if (!this.editingId) return;
    const trimmed = this.editingValue.trim();
    if (trimmed.length > 0) {
      this.rename.emit({ id: this.editingId, title: trimmed });
    }
    this.editingId = null;
    this.editingValue = '';
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editingValue = '';
  }

  ngAfterViewChecked(): void {
    if (!this.focusPendingId) return;
    const match = this.titleInputs?.find((ref) => ref.nativeElement.dataset['id'] === this.focusPendingId);
    if (match) {
      const el = match.nativeElement;
      el.focus();
      el.select();
      this.focusPendingId = null;
      this.cdr.markForCheck();
    }
  }

  private today(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }
  private sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }
}
